import { type ListingProduct, ListingProductCode } from 'src/generated/graphql';

import type { DesktopFeatures } from '../desktop';
import { desktopRenderOrder } from '../desktop/desktop';
import {
  desktopRenderOrderFreemium,
  type DesktopFeaturesFreemium,
} from '../freemium/desktop';
import { mobileProductDetailsFreemium } from '../freemium/mobile';
import {
  listingProductsFreemium,
  type ListingPackageInfoFreemium,
} from '../freemium/products';
import { mobileProductDetails, type MobileFeatures } from '../mobile';
import { isFreemiumExperiment } from '../products';

import type { ListingProductWithPackageInfo } from '.';

interface ListingPackageInfo {
  productCode: ListingProduct['productCode'];
  description: string;
  features: {
    desktop: DesktopFeatures;
    mobile: MobileFeatures;
  };
}

export const listingProducts = [
  {
    productCode: ListingProductCode.SixAdPack,
    description: 'Advertise your business for 6 months',
    features: {
      desktop: {
        price: 'enabled',
        refresh: 'enabled',
        tools: 'enabled',
        duration: 'disabled',
        boostedAds: 'addOn450',
      },
      mobile: {
        calculator: { enabled: true },
        checklist: { enabled: true },
        edit: { enabled: true },
        price: { enabled: true },
        refresh: { enabled: true },
        screening: { enabled: true },
        boostedAds: { enabled: true, label: 'Promote your ad (add-on*)' },
        duration: { enabled: true, label: 'Advertise for 6 months' },
      },
    },
  },
  {
    productCode: ListingProductCode.ListUntilSold,
    description: "Advertise your business until it's sold",
    features: {
      desktop: {
        duration: 'enabled',
        price: 'enabled',
        refresh: 'enabled',
        tools: 'enabled',
        boostedAds: 'addOn650',
      },
      mobile: {
        calculator: { enabled: true },
        checklist: { enabled: true },
        edit: { enabled: true },
        price: { enabled: true },
        refresh: { enabled: true },
        screening: { enabled: true },
        boostedAds: { enabled: true, label: 'Promote your ad (add-on*)' },
        duration: { enabled: true, label: 'Advertise until sold' },
      },
    },
  },
  {
    productCode: ListingProductCode.PayPerEnquiry,
    description: 'Pay only for enquiries you receive',
    features: {
      desktop: {
        price: 'payPerEnquiry',
        duration: 'enabled',
        boostedAds: 'enabled',
        refresh: 'enabled',
        tools: 'enabled',
      },
      mobile: {
        calculator: { enabled: true },
        checklist: { enabled: true },
        edit: { enabled: true },
        price: { enabled: true },
        refresh: { enabled: true },
        screening: { enabled: true },
        boostedAds: { enabled: true, label: 'Promote your ad' },
        duration: { enabled: true, label: 'Advertise until sold' },
      },
    },
  },
] satisfies ListingPackageInfo[];

export type MainListingProduct =
  | ListingProductCode.SixAdPack
  | ListingProductCode.ListUntilSold
  | ListingProductCode.PayPerEnquiry;

export type MainListingProductFreemium =
  | ListingProductCode.Freemium
  | ListingProductCode.ListUntilSold
  | ListingProductCode.Accelerate;

export const hasProductInfo = (
  listingProduct: ListingProduct,
): listingProduct is ListingProductWithPackageInfo => {
  if (isFreemiumExperiment) {
    return listingProductsFreemium.some(
      (x) => listingProduct && x.productCode === listingProduct.productCode,
    );
  }
  return listingProducts.some(
    (x) => listingProduct && x.productCode === listingProduct.productCode,
  );
};

export const getProductInfo = (
  listingProduct: ListingProductWithPackageInfo | ListingPackageInfoFreemium,
) => {
  if (isFreemiumExperiment) {
    return listingProductsFreemium.find(
      (x) => x.productCode === listingProduct.productCode,
    )!;
  }
  return listingProducts.find(
    (x) => x.productCode === listingProduct.productCode,
  )!;
};

export const getMobileProductDetails = (productCode: ListingProductCode) => {
  if (isFreemiumExperiment) {
    return mobileProductDetailsFreemium[
      productCode as MainListingProductFreemium
    ];
  }
  return mobileProductDetails[productCode as MainListingProduct];
};

const desktopFeatureSortCompare = (a: string, b: string): number => {
  // "desktopRenderOrder as string[]" is safe as "indexOf" will return -1
  let indexOfA;
  let indexOfB;
  if (isFreemiumExperiment) {
    indexOfA = (desktopRenderOrderFreemium as string[]).indexOf(a);
    indexOfB = (desktopRenderOrderFreemium as string[]).indexOf(b);
  } else {
    indexOfA = (desktopRenderOrder as string[]).indexOf(a);
    indexOfB = (desktopRenderOrder as string[]).indexOf(b);
  }

  if (indexOfA < indexOfB) {
    return -1;
  } else if (indexOfA > indexOfB) {
    return 1;
  }
  return 0;
};

export const sortDesktopFeatures = <T>(
  features:
    | Record<keyof DesktopFeatures, T>
    | Record<keyof DesktopFeaturesFreemium, T>,
) =>
  Object.entries(features).sort((a, b) =>
    desktopFeatureSortCompare(a[0], b[0]),
  );
